import type { IDadataAddress } from '../model';

type BackendLocationPart = {
  name?: string | null;
  code?: string | null;
  type?: string | null;
  type_short?: string | null;
};

type BackendCity = BackendLocationPart & {
  owner?: BackendLocationPart | null;
};

export type BackendAddressOfOperation = {
  address_text?: string | null;
  postal_code?: string | null;
  flat?: string | null;
  house?: string | null;
  region?: BackendLocationPart | null;
  street?: BackendLocationPart | null;
  area?: BackendLocationPart | null;
  city?: BackendCity | null;
  building?: string | null;
};

const formatLocationWithType = (part?: BackendLocationPart | null) => {
  if (!part?.name) return '';
  return [part.type_short, part.name].filter(Boolean).join(' ').trim();
};

const getCityDataFields = (city?: BackendCity | null): Partial<IDadataAddress['data']> => {
  if (!city?.name) return {};

  if (city.owner?.name) {
    return {
      settlement: city.name ?? '',
      settlement_type: city.type_short ?? '',
      settlement_type_full: city.type ?? '',
      settlement_kladr_id: city.code ?? '',
      settlement_with_type: formatLocationWithType(city),
      city: city.owner.name ?? '',
      city_type: city.owner.type_short ?? '',
      city_type_full: city.owner.type ?? '',
      city_kladr_id: city.owner.code ?? '',
      city_with_type: formatLocationWithType(city.owner)
    };
  }

  return {
    city: city.name ?? '',
    city_type: city.type_short ?? '',
    city_type_full: city.type ?? '',
    city_kladr_id: city.code ?? '',
    city_with_type: formatLocationWithType(city)
  };
};

const buildDadataDataFromBackendAddress = (addr: BackendAddressOfOperation): IDadataAddress['data'] => {
  const region = addr.region;
  const street = addr.street;

  return {
    country: '',
    region_with_type: formatLocationWithType(region),
    region: region?.name ?? '',
    region_type: region?.type_short ?? '',
    region_type_full: region?.type ?? '',
    region_kladr_id: region?.code ?? '',
    street_with_type: formatLocationWithType(street),
    street: street?.name ?? '',
    street_type: street?.type_short ?? '',
    street_type_full: street?.type ?? '',
    street_kladr_id: street?.code ?? '',
    flat: addr.flat ?? '',
    house: addr.house ?? '',
    postal_code: addr.postal_code ?? '',
    block: addr.building ?? '',
    ...getCityDataFields(addr.city)
  } as IDadataAddress['data'];
};

export const isEmptyAddress = (val: unknown): boolean => {
  if (val == null) return true;
  if (typeof val === 'string') return val.trim() === '';

  if (typeof val === 'object') {
    const obj = val as Record<string, unknown>;
    const hasText = Boolean(String(obj.unrestricted_value ?? obj.value ?? '').trim());

    return !hasText && isDadataAddressDataEmpty(obj as IDadataAddress);
  }

  return true;
};

export const isDadataAddressDataEmpty = (address: IDadataAddress | null | undefined): boolean => {
  const data = (address?.data ?? {}) as Partial<IDadataAddress['data']>;

  return !(
    String(data.region_with_type ?? '').trim() ||
    String(data.region ?? '').trim() ||
    String(data.city_with_type ?? '').trim() ||
    String(data.city ?? '').trim() ||
    String(data.settlement ?? '').trim()
  );
};

export const getAddressOfOperationForUsage = (
  address: BackendAddressOfOperation | null | undefined
): IDadataAddress | null => {
  if (!address) return null;

  const unrestricted_value = address.address_text?.trim() ?? '';
  if (!unrestricted_value) return null;

  return {
    value: unrestricted_value,
    unrestricted_value,
    data: buildDadataDataFromBackendAddress(address)
  };
};

export const getAddressForUsage = (objectWithAddress: ReturnType<typeof getAddressForSubmission>) => {
  const address_text = objectWithAddress?.address_text ?? '';
  const city = objectWithAddress?.address_city ?? '';
  const country = objectWithAddress?.address_country ?? '';
  const flat = objectWithAddress?.address_flat ?? '';
  const house = objectWithAddress?.address_house ?? '';
  const postal_code = objectWithAddress?.address_postcode ?? '';
  const region = objectWithAddress?.address_region ?? '';
  const street = objectWithAddress?.address_street ?? '';

  const address =
    objectWithAddress && typeof objectWithAddress === 'object' && 'address' in objectWithAddress
      ? objectWithAddress.address
      : null;

  const data = {
    country,
    region_with_type: region,
    city_with_type: city,
    street_with_type: street,
    flat,
    house,
    postal_code,

    ...(address ?? {})
  };
  const unrestricted_value = address_text
    ? address_text
    : [postal_code, country, region, city, street, flat, house].filter(Boolean).join(', ');

  return { data, unrestricted_value };
};

const getCity = (data: IDadataAddress['data']) => {
  if (data?.settlement && data?.city) {
    return {
      name: data?.settlement ?? null,
      code: data?.settlement_kladr_id ?? null,
      type: data?.settlement_type_full ?? null,
      type_short: data?.settlement_type ?? null,
      owner: {
        name: data?.city ?? null,
        code: data?.city_kladr_id ?? null,
        type: data?.city_type_full ?? null,
        type_short: data?.city_type ?? null
      }
    };
  } else if (data?.settlement || data?.city) {
    return {
      name: (data?.settlement || data?.city) ?? null,
      code: (data?.settlement_kladr_id || data?.city_kladr_id) ?? null,
      type: (data?.settlement_type_full || data?.city_type_full) ?? null,
      type_short: (data?.settlement_type || data?.city_type) ?? null
    };
  }
};

export const getAddressForSubmission = (address: IDadataAddress, withExtention: boolean) => {
  const data = address?.data ?? {};
  const address_text = address?.unrestricted_value ?? null;

  const result = {
    address: {
      address_text,
      area: {
        name: data?.area ?? null,
        code: data?.area_kladr_id ?? null,
        type: data?.area_type_full ?? null,
        type_short: data?.area_type ?? null
      },
      building: data.block ?? null,
      city: getCity(data),
      flat: data.flat ?? null,
      house: data.house ?? null,
      postal_code: data?.postal_code ?? null,
      region: {
        name: data?.region ?? null,
        code: data?.region_kladr_id ?? null,
        type: data?.region_type_full ?? null,
        type_short: data?.region_type ?? null
      },
      street: {
        name: data?.street ?? null,
        code: data?.street_kladr_id ?? null,
        type: data?.street_type_full ?? null,
        type_short: data?.street_type ?? null
      },
      structure: data.block ?? null
    },
    address_city: data?.city_with_type ?? null,
    address_country: data?.country ?? null,
    address_flat: data?.flat ?? null,
    address_house: data?.house ?? null,
    address_postcode: data?.postal_code ?? null,
    address_region: data?.region_with_type ?? null,
    address_street: data?.street_with_type ?? null,
    address_text
  };

  if (!withExtention) {
    const { address: _address, ...rest } = result;
    return rest;
  }
  return result;
};

export const getAddressOfOperationForSubmission = (address: IDadataAddress) => {
  const data = address?.data ?? {};
  const address_text = address?.unrestricted_value ?? null;

  return {
    address_of_operation: {
      address_text,
      area: {
        name: data?.area ?? null,
        code: data?.area_kladr_id ?? null,
        type: data?.area_type_full ?? null,
        type_short: data?.area_type ?? null
      },
      building: data.block ?? null,
      city: getCity(data),
      flat: data.flat ?? null,
      house: data.house ?? null,
      postal_code: data?.postal_code ?? null,
      region: {
        name: data?.region ?? null,
        code: data?.region_kladr_id ?? null,
        type: data?.region_type_full ?? null,
        type_short: data?.region_type ?? null
      },
      street: {
        name: data?.street ?? null,
        code: data?.street_kladr_id ?? null,
        type: data?.street_type_full ?? null,
        type_short: data?.street_type ?? null
      },
      structure: data.block ?? null
    }
  };
};
